import { useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import type { UseAffiliateResult } from "../../hooks/useAffiliate";
import styles from "./AccountSettings.module.css";

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("es", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

interface AccountSettingsProps {
  affiliate: UseAffiliateResult;
}

export const AccountSettings = ({ affiliate }: AccountSettingsProps) => {
  const { status, loading, redeeming, redeemError, redeem } = affiliate;
  const [code, setCode] = useState("");

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    try {
      await redeem(trimmed);
      setCode("");
    } catch {
      // Error is surfaced via redeemError; nothing else to do here.
    }
  };

  return (
    <s-grid gridTemplateColumns="1fr" gap="large-200" alignItems="start">
      {/*
        Las secciones "Tu saldo de Codway", "Tu idioma" y "Tu cobertura" quedan
        comentadas hasta que tengan su conexión con el backend. Solo se entrega
        la sección de afiliado por ahora.
      */}

      <s-stack direction="block" gap="small-200">
        <s-heading>Tu código de afiliado:</s-heading>

        <s-box
          background="base"
          border="small subdued solid"
          borderRadius="large-100"
          padding="large-200"
        >
          {loading ? (
            <s-spinner accessibility-label="Cargando código de afiliado…" />
          ) : status && status.has_affiliate ? (
            <s-stack direction="block" gap="base">
              <span className={styles.affiliateHeader}>
                <s-text color="subdued">{status.affiliate_name}</s-text>
                <span className={styles.inlineBadge}>
                  {" "}
                  <s-badge tone="success">Registrado</s-badge>
                </span>
              </span>

              <s-stack direction="block" gap="small-100">
                <s-text>
                  Código:{" "}
                  <s-text type="strong">{status.code.toUpperCase()}</s-text>
                </s-text>
                <s-text>
                  Pedidos bonus este mes:{" "}
                  <s-text type="strong">
                    {status.current_period_remaining} /{" "}
                    {status.bonus_orders_per_period}
                  </s-text>
                </s-text>
                <s-text>
                  Periodos restantes:{" "}
                  <s-text type="strong">
                    {Math.max(
                      0,
                      status.bonus_period_count - status.periods_completed
                    )}{" "}
                    / {status.bonus_period_count}
                  </s-text>
                </s-text>
                {status.benefit_expires_after && (
                  <s-text color="subdued">
                    El beneficio vence el{" "}
                    {formatDate(status.benefit_expires_after)}.
                  </s-text>
                )}
              </s-stack>
            </s-stack>
          ) : (
            <s-stack direction="block" gap="base">
              <span className={styles.affiliateHeader}>
                <s-text color="subdued">Registra tu código de afiliado</s-text>
                <span className={styles.inlineBadge}>
                  {" "}
                  <s-badge>No registrado</s-badge>
                </span>
              </span>

              <s-text-field
                label="Ingresar código"
                labelAccessibilityVisibility="visible"
                placeholder="Ingresa tu código"
                value={code}
                error={redeemError ?? undefined}
                onInput={(event: CallbackEvent<"s-text-field">) =>
                  setCode(event.currentTarget.value)
                }
              />

              <s-button
                variant="primary"
                onClick={handleRedeem}
                loading={redeeming}
                disabled={redeeming || code.trim().length === 0}
              >
                Canjear
              </s-button>
            </s-stack>
          )}
        </s-box>
      </s-stack>
    </s-grid>
  );
};
